/* eslint-disable import/no-default-export */
import React, { useEffect, useCallback } from 'react';
import {
  Action,
  ActionSubject,
  ActionSubjectId,
  Source,
  SourceType,
} from '@trello/atlassian-analytics';
import { track, trackScreenEvent, SnowplowContext } from './Analytics';

import { MigrationResponse } from './AtlassianAccountMigrationClient';
import { InAppMigrationDialog } from './InAppMigrationDialog';
import styles from './AtlassianAccountMigrationDialog.less';
import { EmailMigrationDialog } from './EmailMigrationDialog';
import { Verb, Noun } from '@trello/analytics';

const illustrations = {
  main: {
    full: require('resources/images/aa-migration-wizard/illustration-main.svg'),
    mobile: require('resources/images/aa-migration-wizard/illustration-main-mobile.svg'),
  },
  email: {
    full: require('resources/images/aa-migration-wizard/illustration-email.svg'),
    mobile: require('resources/images/aa-migration-wizard/illustration-email-mobile.svg'),
  },
};

enum ScreenType {
  LOG_IN = 'logIn',
  CREATE_ACCOUNT = 'createAccount',
  CHECK_INBOX = 'checkInbox',
}

interface AtlassianAccountMigrationDialogProps {
  onDismiss: () => void;
  email: string;
  changeEmailButton?: React.ReactElement;
  analyticsContext: object;
  migrationResponse: MigrationResponse;
  getSource?: () => SourceType;
}

const fetchText = async (url: string) => {
  const response = await window.fetch(url);
  if (response?.ok) {
    return response.text();
  }
};

const illustrationMap: Map<string, HTMLDivElement> = new Map();
export const fetchIllustration = (key: string) => {
  const existing = illustrationMap.get(key);
  if (existing) {
    return existing;
  }
  const illustration = document.createElement('div');
  illustration.className = styles.illustration;
  illustrationMap.set(key, illustration);
  // we do not need to await, as the HTML element has already been created
  fetchText(key).then((content) => {
    if (content) {
      illustration.innerHTML = content;
    }
  });
  return illustration;
};

const getAnalyticsSource = (screen: ScreenType) => {
  switch (screen) {
    case ScreenType.CHECK_INBOX:
      return Source.aaMigrationWizardCheckInboxScreen;
    case ScreenType.CREATE_ACCOUNT:
      return Source.aaMigrationWizardCreateScreen;
    case ScreenType.LOG_IN:
      return Source.aaMigrationWizardLogInScreen;
    default:
      return Source.unknown;
  }
};

export const AtlassianAccountMigrationDialog: React.FunctionComponent<AtlassianAccountMigrationDialogProps> = ({
  email,
  changeEmailButton,
  onDismiss,
  analyticsContext,
  migrationResponse,
  // For overriding in stories
  getSource = getAnalyticsSource,
}) => {
  useEffect(() => {
    // preload images
    fetchIllustration(illustrations.main.full);
    fetchIllustration(illustrations.email.full);
    fetchIllustration(illustrations.main.mobile);
    fetchIllustration(illustrations.email.mobile);
  });

  const screenToDisplay = migrationResponse.aaExists
    ? ScreenType.LOG_IN
    : migrationResponse.emailVerificationRequired
    ? ScreenType.CHECK_INBOX
    : ScreenType.CREATE_ACCOUNT;

  const analyticsSource = getSource(screenToDisplay);

  useEffect(() => {
    trackScreenEvent(analyticsSource, analyticsContext as SnowplowContext);
  }, [analyticsSource, analyticsContext]);

  const closeDialog = useCallback(() => {
    onDismiss();
    track({
      verb: Verb.DISMISSES,
      noun: Noun.AA_MIGRATION_WIZARD,
      method: 'by dismissing the migration dialog',
      context: analyticsContext as SnowplowContext,
      source: analyticsSource,
      action: Action.closed,
      actionSubject: ActionSubject.overlay,
      actionSubjectId: ActionSubjectId.emailHygieneWizard,
    });
  }, [onDismiss, analyticsSource, analyticsContext]);

  switch (screenToDisplay) {
    case ScreenType.LOG_IN:
    case ScreenType.CREATE_ACCOUNT:
      return (
        <InAppMigrationDialog
          closeDialog={closeDialog}
          aaExists={migrationResponse.aaExists}
          migrationUrl={migrationResponse.redirect_url}
          image={fetchIllustration(illustrations.main.full)}
          mobileImage={fetchIllustration(illustrations.main.mobile)}
        />
      );
    case ScreenType.CHECK_INBOX:
      return (
        <EmailMigrationDialog
          email={email}
          changeEmailButton={changeEmailButton}
          closeDialog={closeDialog}
          image={fetchIllustration(illustrations.email.full)}
          mobileImage={fetchIllustration(illustrations.email.mobile)}
        />
      );
    default:
      return null;
  }
};

export default AtlassianAccountMigrationDialog;
